/**
 * Officers Data Structure
 *
 * Stores profile information for all NCESP leadership officers
 * Used by the Leadership page to display officer cards and biographies
 */

import markLudwigImage from '@/assets/mark-ludwig.jpg'
import danielMartinImage from '@/assets/leadership-daniel-martin.jpg'
import markReelImage from '@/assets/leadership-mark-reel.jpg'

/**
 * Officer Profile Interface
 */
export interface Officer {
  /**
   * Unique identifier for the officer
   */
  id: string

  /**
   * Full name of the officer
   */
  name: string

  /**
   * Official title/position
   */
  title: string

  /**
   * Full biography (supports multiple paragraphs separated by \n\n)
   */
  biography: string

  /**
   * Array of key achievement bullet points
   */
  keyAchievements: string[]

  /**
   * Professional affiliations and organizations
   */
  affiliations: string[]

  /**
   * Optional path to profile image
   */
  image?: string

  /**
   * Path to profile image for card display
   */
  profileImage?: string

  /**
   * Display order on the page (lower numbers appear first)
   */
  order: number
}

/**
 * Mark Ludwig - Founder and President
 */
const markLudwigOfficer: Officer = {
  id: 'mark-ludwig',
  name: 'Mark Ludwig',
  title: 'Founder & President',
  biography: `Mark Ludwig is a passionate advocate for equal shared parenting and father's rights. He founded the National Council for Equal Shared Parenting (NCESP) to address the systemic biases in family courts that separate children from fit, loving parents.

With decades of experience in grassroots advocacy and policy reform, Mark has worked tirelessly to build coalitions across political divides. His mission is to ensure that every child has the fundamental right to a meaningful relationship with both parents, unless there are proven safety concerns.

Mark's personal journey through the family court system ignited his dedication to reform. He witnessed firsthand how outdated laws and judicial discretion can devastate families and harm children. This experience transformed him into a vocal advocate for legislative change at both state and federal levels.

Under his leadership, NCESP has become a leading voice in the equal parenting movement, working to enact presumptive 50/50 shared parenting laws across the United States. Mark believes that equal parenting is not just a policy issue—it's a fundamental civil rights and children's welfare issue.`,
  keyAchievements: [
    'University of Central Missouri – Triple Major in Marketing, Finance, Management',
    'President, Pi Omicron Delta Honor Society',
    'Eagle Scout',
    'Former Candidate for U.S. Congress',
    'Author "Making a Difference – The Insider\'s Guide to Passing State Legislation"',
    'Collaborative Author of the Bestseller "Building Bridges of Hope – Overcoming the Trauma of Family Court"',
    'Featured in the family court documentary "We the Parents"',
    'Founder, Americans for Equal Shared Parenting',
    'President, National Council for Equal Shared Parenting',
    "Legislative Director, The Fathers' Rights Movement",
    'Nationally recognized political adviser and trainer',
    'Featured on "America\'s Thought Leaders", "The Mike Huckabee Show" and "The Pulse of St. Louis"',
    'Over 120 million social media views raising awareness of the need for equal shared parenting',
  ],
  affiliations: [
    'Leadership Institute',
    'Former Missouri Small Business Policy Council for Commerce & Industry',
    "Former Board of Directors, Angels' Arms Foundation for foster children",
    'Policy Advisor, Family Reunion USA',
    'Policy Advisor, The Boys Initiative',
    "Policy Advisor, The Men's Collaborative",
    'American Legislative Exchange Council – HHS Model Policy Task Force',
    'Council for National Policy',
    'National Association of Christian Lawmakers',
    'Phyllis Schlafly Eagle Council',
  ],
  image: '/images/officers/mark-ludwig.jpg',
  profileImage: markLudwigImage,
  order: 1,
}

/**
 * Daniel Martin - Advocacy Leadership
 */
const danielMartinOfficer: Officer = {
  id: 'daniel-martin',
  name: 'Daniel Martin',
  title: 'Advocacy Leadership',
  biography: `Daniel, a 2004 Electrical Engineering graduate from Mississippi State University, serves as the Director of System Engineering and leads the staking department at Coast Electric Power Association. With over 21 years of dedicated service, he is passionate about reliability, innovation, and serving his community.

He is active at Bible Baptist Church in Gulfport, MS. He is also a proud father of Briggs and Mara. His extensive experience with people and project management enables him to effectively advocate for children and families, making a meaningful difference.`,
  keyAchievements: [
    'Director of System Engineering at Coast Electric Power Association',
    'Over 21 years of service in electrical engineering and power systems',
    'Mississippi State University Electrical Engineering graduate (2004)',
    'Completed Jack Hatcher Engineering Entrepreneurship Certificate Program at Mississippi State University',
    'Extensive experience in people and project management',
    'Active community member at Bible Baptist Church in Gulfport, MS',
  ],
  affiliations: [],
  profileImage: danielMartinImage,
  order: 2,
}

/**
 * Mehdi Akacem - Advocacy Leadership
 */
const mehdiAkacemOfficer: Officer = {
  id: 'mehdi-akacem',
  name: 'Mehdi Akacem',
  title: 'Advocacy Leadership',
  biography: `Mehdi Akacem was born in southern Connecticut to an immigrant father and a world-traveler mother, both of whom were small business owners. Growing up, his parents strongly supported his education and instilled in him a resolute motive to chase his dreams, which he was fortunate to do by graduating from the United States Naval Academy in 1999 and becoming a Naval Aviator.

Over 26 years in uniform, Mehdi has deployed worldwide, flying the EA-18G Growler and graduating from the US Naval Test Pilot School. He currently holds the rank of Captain, and is assigned to the F-35 Joint Strike Fighter Program Office in Arlington, VA. He shares two wonderful boys, ages six and eight, with his co-parent, also a military officer.

Note: Mehdi is a member of the United States Navy, but his advocacy and participation with this organization is in a strictly personal capacity as a private citizen. His participation in the work of NCESP reflects his own personal views and experiences and does not represent or imply the endorsement, sponsorship, or official position of the Department of Defense, the Department of the Navy, or any component thereof. Any reference to his military service is solely for purposes of background and context.`,
  keyAchievements: [
    'United States Naval Academy graduate (1999)',
    'Naval Aviator with over 26 years of service',
    'Electronic Warfare Officer (EWO) in the Growler - a backseater - not a pilot',
    'EA-18G Growler pilot with worldwide deployments',
    'US Naval Test Pilot School graduate',
    'Current rank of Captain, assigned to F-35 Joint Strike Fighter Program Office',
  ],
  affiliations: [],
  order: 3,
}

/**
 * Mark Reel Jr. - Advocacy Leadership
 */
const markReelOfficer: Officer = {
  id: 'mark-reel',
  name: 'Mark Reel Jr.',
  title: 'Advocacy Leadership',
  biography: `Mark Reel Jr. is the Founder and CEO of Reel Fathers Rights APC, where he leverages his legal expertise to advocate for fathers' rights in California. Licensed to practice law in the state of California, Mark's dedication to family law stems from his own personal experience navigating the family court system.

Since founding Reel Fathers Rights APC in 2021, Mark has built RFR into a national brand. In 2025, Reel Fathers Rights was named the fastest growing law firm in the United States by Inc and was listed as the 13th fastest growing privately held company in the United States on the Inc 5000 list.

Prior to establishing Reel Fathers Rights, Mark spent nearly a decade as an executive in the sports and fitness industries. However, driven by a passion to help others facing similar challenges, he returned to family law and has since become a nationally recognized figure in the Fathers Rights Movement.

Mark is the author of the bestselling book, "The Fathers Rights Playbook," and hosts a podcast by the same name.

You can learn more about Mark and Reel Fathers Rights at www.ReelFathersRights.com`,
  keyAchievements: [
    'Founder and CEO of Reel Fathers Rights APC',
    'Licensed attorney in the State of California',
    'Inc 5000 #13 fastest growing privately held company in the US (2025)',
    'Fastest growing law firm in the United States (Inc, 2025)',
    'Author of bestselling book "The Fathers Rights Playbook"',
    'Host of "The Fathers Rights Playbook" podcast',
    'Nearly a decade as executive in sports and fitness industries',
  ],
  affiliations: ['Reel Fathers Rights APC'],
  profileImage: markReelImage,
  order: 4,
}

/**
 * Anthony Michael Perez - Advocacy Leadership
 */
const anthonyPerezOfficer: Officer = {
  id: 'anthony-perez',
  name: 'Anthony Michael Perez',
  title: 'Advocacy Leadership',
  biography: `Anthony Michael Perez is a United States Army veteran and a dedicated father of two sons who live 1,500 miles apart. Drawing from his personal experience that shaped his commitment to advocacy, Anthony has become an active voice for reform, channeling his efforts to legislative initiatives in Idaho and California. His state level activities focus on policies to promote fairness, and allow children to have equal access to both parents after separation or divorce. At the federal level, his work focuses on reforming Title IV-D.`,
  keyAchievements: [
    'United States Army veteran',
    'Father of two sons',
    'Active advocate for family law reform in Idaho and California',
    'Focused on policies promoting equal parental access after separation or divorce',
    'Federal-level advocacy for Title IV-D reform',
  ],
  affiliations: [],
  order: 5,
}

/**
 * Export all officers in display order
 */
export const officers: Officer[] = [
  markLudwigOfficer,
  danielMartinOfficer,
  mehdiAkacemOfficer,
  markReelOfficer,
  anthonyPerezOfficer,
]

/**
 * Get officer by ID
 */
export const getOfficerById = (id: string): Officer | undefined => {
  return officers.find(officer => officer.id === id)
}

/**
 * Get officers sorted by display order
 */
export const getOfficersByOrder = (): Officer[] => {
  return [...officers].sort((a, b) => a.order - b.order)
}
